import type { BoxProps } from '@hover/blueprint';
import { Box, Link, useTheme } from '@hover/blueprint';
import { heightHeader } from '@hover/tokens-brand';
import blackHoverLogo from '@hover/tokens-brand/assets/logo-full--black.svg?url';
import whiteHoverLogo from '@hover/tokens-brand/assets/logo-full--white.svg?url';
import React from 'react';

import { useIsSharedView } from 'src/modules/renderings/hooks';
import { useFocusedView } from 'src/modules/renderings/hooks/useFocusedView';
import { useHeaderPortal } from 'src/modules/renderings/hooks/useHeaderPortal';
import { Image } from 'src/shared/components';
import { useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';
import { External as ExternalRoute, Route } from 'src/shared/route';

import { LoginButton } from './LoginButton';

export const HEADER_HEIGHT = 72;

interface HeaderProps {
  routeKey?: keyof typeof Route | keyof typeof ExternalRoute;
}

const Routes = { ...ExternalRoute, ...Route };

const Header = ({ routeKey = 'ROOT' }: HeaderProps) => {
  const t = useTranslate(TranslationNamespace.SHARED);
  const isSharedView = useIsSharedView();
  const { isFocusedView } = useFocusedView();
  const headerPortal = useHeaderPortal();

  const { colors } = useTheme();
  const headerPortalDisplay = isFocusedView ? 'flex' : 'none';
  let backgroundColor = 'primary.600';
  let borderBottom: BoxProps['borderBottom'];
  let hoverLogo = whiteHoverLogo;

  if (isSharedView) {
    backgroundColor = 'neutral.0';
    borderBottom = `solid 1px ${colors.neutral['200']}`;
    hoverLogo = blackHoverLogo;
  }
  if (isFocusedView) {
    backgroundColor = 'neutral.900';
    borderBottom = `solid 1px ${colors.neutral['700']}`;
    hoverLogo = whiteHoverLogo;
  }

  return (
    <Box
      alignItems="center"
      as="header"
      backgroundColor={backgroundColor}
      borderBottom={borderBottom}
      height={`${HEADER_HEIGHT}px`}
      justifyContent="space-between"
      justifyItems="center"
      padding={400}
      transition="background 800ms"
    >
      <Link
        aria-label={t('navigation.navigateToHome')}
        href={Routes[routeKey].path}
        testId="hover-logo"
      >
        <Box flexDirection="row" paddingX={600}>
          <Image
            alt={t('logoWithText', { name: 'HOVER' })}
            height={heightHeader}
            paddingTop={200}
            src={hoverLogo}
          />
        </Box>
      </Link>
      {isSharedView && !isFocusedView && <LoginButton />}
      <Box display={headerPortalDisplay} ref={headerPortal.ref} />
    </Box>
  );
};

export { Header };
